import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft, 
  Music, 
  Plus, 
  Upload, 
  Play, 
  Pause,
  Trash2,
  Edit,
  Save,
  Settings,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  FileQuestion
} from 'lucide-react';
import { toast } from 'sonner';
import api, { BACKEND_BASE_URL } from '../lib/api';
const API = '/api';

const STYLES = ['gospel', 'blues', 'worship', 'country', 'jazz', 'rock'];
const KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// Format time as mm:ss
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AdminShedTracksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const audioPreviewRef = useRef(null);
  
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTrack, setEditingTrack] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    key: '',
    tempo: '',
    style: '',
    audio_url: '',
    stored_filename: '',
    duration: 0,
    is_published: true
  });
  
  // Preview state
  const [previewTrack, setPreviewTrack] = useState(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, track: null });
  
  // Verify/Sync state
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResults, setVerifyResults] = useState(null);
  const [relinkForm, setRelinkForm] = useState({ open: false, file: null });

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const response = await api.get(`/shed-tracks/admin`);
      setTracks(response.data);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      toast.error('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      key: '',
      tempo: '',
      style: '',
      audio_url: '',
      stored_filename: '',
      duration: 0,
      is_published: true
    });
    setEditingTrack(null);
  };

  const handleEdit = (track) => {
    setEditingTrack(track);
    setFormData({
      title: track.title || '',
      key: track.key || '',
      tempo: track.tempo || '',
      style: track.style || '',
      audio_url: track.playback_url || track.audio_url || '',
      stored_filename: track.stored_filename || '',
      duration: track.duration || 0,
      is_published: track.is_published !== false
    });
    setShowForm(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.type.includes('audio')) {
      toast.error('Please select an audio file');
      return;
    }
    
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setUploading(true);
    
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(',')[1];
        
        try {
          const response = await api.post(
            '/shed-tracks/upload',
            { file_data: base64Data, file_name: file.name }
          );
          
          // Update form with stored_filename and playback_url
          setFormData(prev => ({
            ...prev,
            audio_url: response.data.playback_url,
            stored_filename: response.data.stored_filename
          }));
          
          // Get duration from uploaded file
          const audioUrl = response.data.playback_url.startsWith('/api/') 
            ? `${BACKEND_BASE_URL}${response.data.playback_url}`
            : `${BACKEND_BASE_URL}/api${response.data.playback_url}`;
          const audio = new Audio(audioUrl);
          audio.onloadedmetadata = () => {
            setFormData(prev => ({
              ...prev,
              duration: Math.round(audio.duration)
            }));
          };
          
          toast.success('Audio uploaded successfully');
        } catch (error) {
          console.error('Upload error:', error);
          toast.error('Failed to upload audio');
        } finally {
          setUploading(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Failed to read file');
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!formData.audio_url && !formData.stored_filename) {
      toast.error('Please upload an audio file');
      return;
    }
    
    setSaving(true);
    
    try {
      const data = {
        title: formData.title.trim(),
        key: formData.key || null,
        tempo: formData.tempo ? parseInt(formData.tempo) : null,
        style: formData.style || null,
        audio_url: formData.audio_url,
        stored_filename: formData.stored_filename || null,
        playback_url: formData.audio_url,
        duration: formData.duration || null,
        is_published: formData.is_published
      };
      
      if (editingTrack) {
        await api.put(
          `/shed-tracks/${editingTrack.track_id}`,
          data
        );
        toast.success('Track updated');
      } else {
        await api.post(`/shed-tracks`, data);
        toast.success('Track created and published!');
      }
      
      setShowForm(false);
      resetForm();
      fetchTracks();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save track');
    } finally {
      setSaving(false);
    }
  };
  
  // Verify/Sync functions
  const handleVerify = async () => {
    setVerifyLoading(true);
    try {
      const response = await api.get(`/shed-tracks/verify`);
      setVerifyResults(response.data);
      setShowVerifyDialog(true);
    } catch (error) {
      console.error('Verify error:', error);
      toast.error('Failed to verify tracks');
    } finally {
      setVerifyLoading(false);
    }
  };
  
  const handleRelink = async (orphanFile) => {
    setRelinkForm({ open: true, file: orphanFile, title: '', key: '', tempo: '', style: '' });
  };
  
  const submitRelink = async () => {
    if (!relinkForm.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      await api.post(
        '/shed-tracks/relink',
        {
          stored_filename: relinkForm.file.filename,
          title: relinkForm.title.trim(),
          key: relinkForm.key || null,
          tempo: relinkForm.tempo ? parseInt(relinkForm.tempo) : null,
          style: relinkForm.style || null,
          is_published: true
        }
      );
      toast.success(`Track "${relinkForm.title}" restored!`);
      setRelinkForm({ open: false, file: null });
      handleVerify(); // Refresh verify results
      fetchTracks(); // Refresh track list
    } catch (error) {
      console.error('Relink error:', error);
      const msg = error.response?.data?.message || 'Failed to restore track';
      toast.error(msg);
    }
  };

  const handleDelete = async (track) => {
    try {
      await api.delete(`/shed-tracks/${track.track_id}`);
      toast.success('Track deleted');
      setDeleteDialog({ open: false, track: null });
      fetchTracks();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete track');
    }
  };

  const togglePreview = (track) => {
    if (previewTrack?.track_id === track.track_id && isPreviewPlaying) {
      audioPreviewRef.current?.pause();
      setIsPreviewPlaying(false);
    } else {
      setPreviewTrack(track);
      if (audioPreviewRef.current) {
        audioPreviewRef.current.src = `${BACKEND_BASE_URL}${track.audio_url}`;
        audioPreviewRef.current.play().catch(e => console.error(e));
        setIsPreviewPlaying(true);
      }
    }
  };

  const handlePreviewEnded = () => {
    setIsPreviewPlaying(false);
  };

  return (
    <div className="min-h-screen bg-ss-bg">
      <div className="noise-overlay"></div>

      {/* Hidden audio for preview */}
      <audio ref={audioPreviewRef} onEnded={handlePreviewEnded} />

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Admin: Shed Tracks
              </h1>
              <p className="text-xs text-ss-muted">Manage backing tracks</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleVerify}
              disabled={verifyLoading}
              variant="outline"
              data-testid="verify-tracks-btn"
              className="border-ss-border text-ss-text hover:bg-ss-surface"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${verifyLoading ? 'animate-spin' : ''}`} />
              Verify / Sync
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              data-testid="add-track-btn"
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Track
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : tracks.length === 0 ? (
          <Card className="bg-ss-surface border-ss-border">
            <CardContent className="py-12 text-center">
              <Music className="w-12 h-12 text-ss-muted mx-auto mb-4" />
              <p className="text-ss-muted mb-4">No tracks yet. Add your first track to get started.</p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Track
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {tracks.map(track => (
              <Card 
                key={track.track_id}
                className={`bg-ss-surface border-ss-border ${!track.is_published ? 'opacity-60' : ''}`}
                data-testid={`admin-track-${track.track_id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    {/* Play Preview */}
                    <Button
                      onClick={() => togglePreview(track)}
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full border-ss-border"
                      data-testid={`preview-${track.track_id}`}
                    >
                      {previewTrack?.track_id === track.track_id && isPreviewPlaying ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4 ml-0.5" />
                      )}
                    </Button>
                    
                    {/* Track Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-ss-text">{track.title}</h3>
                        {!track.is_published && (
                          <span className="text-xs px-2 py-0.5 bg-ss-muted/20 text-ss-muted rounded">
                            Unpublished
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {track.key && (
                          <span className="text-xs text-ss-muted">Key: {track.key}</span>
                        )}
                        {track.tempo && (
                          <span className="text-xs text-ss-muted">{track.tempo} BPM</span>
                        )}
                        {track.style && (
                          <span className="text-xs text-ss-muted capitalize">{track.style}</span>
                        )}
                        <span className="text-xs text-ss-muted">{formatTime(track.duration)}</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEdit(track)}
                        variant="outline"
                        size="sm"
                        className="border-ss-border text-ss-text"
                        data-testid={`edit-${track.track_id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => setDeleteDialog({ open: true, track })}
                        variant="outline"
                        size="sm"
                        className="border-ss-border text-ss-red hover:bg-ss-red/10"
                        data-testid={`delete-${track.track_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Track Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingTrack ? 'Edit Track' : 'Add New Track'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Audio Upload */}
            <div>
              <Label className="text-ss-muted">Audio File *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="mt-1 flex items-center gap-3">
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  variant="outline"
                  className="border-ss-border text-ss-text"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading...' : 'Upload MP3'}
                </Button>
                {formData.audio_url && (
                  <span className="text-sm text-ss-cyan">Audio uploaded</span>
                )}
              </div>
            </div>
            
            {/* Title */}
            <div>
              <Label className="text-ss-muted">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Gospel Groove in E"
                className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                data-testid="track-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Key */}
              <div>
                <Label className="text-ss-muted">Key</Label>
                <Select 
                  value={formData.key} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, key: value }))}
                >
                  <SelectTrigger className="mt-1 bg-ss-bg border-ss-border text-ss-text">
                    <SelectValue placeholder="Select key" />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    {KEYS.map(key => (
                      <SelectItem key={key} value={key} className="text-ss-text">{key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tempo */}
              <div>
                <Label className="text-ss-muted">Tempo (BPM)</Label>
                <Input
                  type="number"
                  value={formData.tempo}
                  onChange={(e) => setFormData(prev => ({ ...prev, tempo: e.target.value }))}
                  placeholder="85"
                  className="mt-1 bg-ss-bg border-ss-border text-ss-text"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Style */}
              <div>
                <Label className="text-ss-muted">Style</Label>
                <Select 
                  value={formData.style} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, style: value }))}
                >
                  <SelectTrigger className="mt-1 bg-ss-bg border-ss-border text-ss-text">
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    {STYLES.map(style => (
                      <SelectItem key={style} value={style} className="text-ss-text capitalize">{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Published Toggle */}
            <div className="flex items-center justify-between">
              <Label className="text-ss-muted">Published</Label>
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
              />
            </div>
            
            {/* Duration (readonly) */}
            {formData.duration > 0 && (
              <div className="text-sm text-ss-muted">
                Duration: {formatTime(formData.duration)}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="border-ss-border text-ss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || uploading}
              data-testid="save-track-btn"
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Track'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, track: null })}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Track</DialogTitle>
          </DialogHeader>
          <p className="text-ss-muted">
            Are you sure you want to delete "{deleteDialog.track?.title}"? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, track: null })}
              className="border-ss-border text-ss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(deleteDialog.track)}
              data-testid="confirm-delete-btn"
              className="bg-ss-red hover:bg-ss-red/90 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify/Sync Dialog */}
      <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Verify / Sync Tracks
            </DialogTitle>
          </DialogHeader>
          
          {verifyResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-ss-bg rounded-lg">
                <div>
                  <p className="text-xs text-ss-muted">DB Tracks</p>
                  <p className="text-lg font-bold text-ss-text">{verifyResults.summary?.total_db_tracks || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-ss-muted">With Files</p>
                  <p className="text-lg font-bold text-green-400">{verifyResults.summary?.tracks_with_files || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-ss-muted">Missing Files</p>
                  <p className="text-lg font-bold text-red-400">{verifyResults.summary?.tracks_missing_files || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-ss-muted">Orphan Files</p>
                  <p className="text-lg font-bold text-yellow-400">{verifyResults.orphan_files?.length || 0}</p>
                </div>
              </div>
              
              {/* DB Tracks Status */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Music className="w-4 h-4" />
                  DB Track Status
                </h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {verifyResults.db_tracks?.map((track, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm p-2 bg-ss-bg rounded">
                      {track.file_exists ? (
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className="flex-1 truncate">{track.title}</span>
                      <span className={`text-xs ${track.file_exists ? 'text-green-400' : 'text-red-400'}`}>
                        {track.file_exists ? track.file_location : 'Missing'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Orphan Files */}
              {verifyResults.orphan_files?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-yellow-400">
                    <FileQuestion className="w-4 h-4" />
                    Orphan Files (on disk but not in DB)
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {verifyResults.orphan_files.map((file, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm p-2 bg-ss-bg rounded">
                        <FileQuestion className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <span className="flex-1 truncate font-mono text-xs">{file.filename}</span>
                        <span className="text-xs text-ss-muted">{file.location}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRelink(file)}
                          className="h-7 text-xs border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                        >
                          Restore
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-ss-muted">
                Storage: {verifyResults.summary?.storage_dir} 
                ({verifyResults.summary?.persistent_files_count || 0} files)
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowVerifyDialog(false)}
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relink Form Dialog */}
      <Dialog open={relinkForm.open} onOpenChange={(open) => !open && setRelinkForm({ open: false, file: null })}>
        <DialogContent className="bg-ss-surface border-ss-border text-ss-text">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Restore Orphan File</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-ss-bg rounded-lg">
              <p className="text-xs text-ss-muted mb-1">File</p>
              <p className="font-mono text-sm text-ss-text truncate">{relinkForm.file?.filename}</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-ss-text">Title *</Label>
              <Input
                value={relinkForm.title || ''}
                onChange={(e) => setRelinkForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Track title"
                className="bg-ss-bg border-ss-border text-ss-text"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-ss-text">Key</Label>
                <Select
                  value={relinkForm.key || ''}
                  onValueChange={(v) => setRelinkForm(prev => ({ ...prev, key: v }))}
                >
                  <SelectTrigger className="bg-ss-bg border-ss-border text-ss-text">
                    <SelectValue placeholder="Select key" />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    {KEYS.map(k => (
                      <SelectItem key={k} value={k} className="text-ss-text">{k}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-ss-text">Style</Label>
                <Select
                  value={relinkForm.style || ''}
                  onValueChange={(v) => setRelinkForm(prev => ({ ...prev, style: v }))}
                >
                  <SelectTrigger className="bg-ss-bg border-ss-border text-ss-text">
                    <SelectValue placeholder="Select style" />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    {STYLES.map(s => (
                      <SelectItem key={s} value={s} className="text-ss-text capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-ss-text">Tempo (BPM)</Label>
              <Input
                type="number"
                value={relinkForm.tempo || ''}
                onChange={(e) => setRelinkForm(prev => ({ ...prev, tempo: e.target.value }))}
                placeholder="120"
                className="bg-ss-bg border-ss-border text-ss-text"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRelinkForm({ open: false, file: null })}
              className="border-ss-border text-ss-text"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRelink}
              className="bg-ss-cyan hover:bg-ss-cyan/90 text-black"
            >
              Restore Track
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminShedTracksPage;
